/**
 * The header and footer, as one definition both pages render from.
 *
 * ## Why this file exists
 *
 * There are two documents on this site — the app and `/glossary` — and the
 * chrome around them used to be written twice: once as a template literal in
 * `main.ts` and once as hand-written HTML in `glossary.html`. Within a day the
 * two had drifted in four separate ways, each found by the owner rather than by
 * a test: the glossary had a different set of nav items, no theme toggle, a
 * wordmark set in the body face instead of the display face, and a footer
 * missing the Hugging Face thanks entirely.
 *
 * None of those were mistakes anybody made. They are what two copies of one
 * thing do. So there is one copy now, and the copy is a function.
 *
 * ## Why a string and not a DOM builder
 *
 * `/glossary` has no runtime — it is generated at build time by
 * `build/glossary-plugin.ts`, inside Vite's config process, where there is no
 * `document`. Everything here is therefore pure string work over its arguments,
 * the same constraint `render/card-map.ts` lives under and for the same reason:
 * a shared thing may not assume the richer of its two environments.
 *
 * ## What is allowed to differ
 *
 * Only what the page genuinely is, expressed as arguments rather than as a
 * second version:
 *
 * - **The tagline** — "safetensors, read in place" on the app, "glossary" on
 *   the glossary. It is the one part of the header that says where you are.
 * - **Whether the wordmark is the page's `<h1>`** — it is on the app, where the
 *   brand is what the document is about. On the glossary the `<h1>` is
 *   "Glossary", and a second one would leave the outline claiming the page is
 *   about the brand.
 * - **Where the etiquette explanation lives** — a tooltip in the app, a jump
 *   link on the glossary, where the full text is already a few hundred pixels
 *   down the same page. Same glyph, same place, same accessible name.
 *
 * Everything else is identical by construction.
 */

import { explainIcon, explainLink } from "./explain.js";
import { logoSvg } from "./logo.js";

/** The nav items, in the order they appear. Both are on both pages. */
export type NavItem = "about" | "glossary";

export interface ChromeOptions {
  /** What sits after the wordmark, behind the divider rule. */
  readonly tagline: string;
  /**
   * Whether the brand lockup is this document's `<h1>`.
   *
   * The `.brand` rule pins the font size either way, so nothing moves visually
   * — this is purely about the heading outline.
   */
  readonly brandIsHeading: boolean;
  /** Which nav item is the page being rendered, for `aria-current`. */
  readonly current?: NavItem;
  /**
   * Where the footer's etiquette explanation should send a reader.
   *
   * Omitted on the app: there the glyph opens the tooltip, wired by
   * `wireExplain`. Given a fragment on the glossary, where the same text is on
   * the page already and a popup repeating it would be absurd — and where the
   * page must keep working with no script at all.
   */
  readonly etiquetteHref?: string;
}

/**
 * The header bar.
 *
 * The theme toggle ships `hidden` and `mountThemeToggle` reveals it, so the
 * control is visible exactly when it is wired. That matters on the glossary,
 * which is a static document a reader may well receive with no JavaScript
 * running: a dead button is worse than no button. On the app it is invisible
 * either way — nothing else renders without a script there.
 */
export function headerHtml(options: ChromeOptions): string {
  const tag = options.brandIsHeading ? "h1" : "span";
  return `<header class="app-header">
    <${tag} class="brand">
      <a class="brand-link" href="/" aria-label="TensorLens home">
        ${logoSvg()}
        <span class="brand-name">Tensor<em>Lens</em></span>
      </a>
    </${tag}>
    <span class="brand-tag">${escapeAttribute(options.tagline)}</span>
    <span class="header-spacer"></span>
    ${navLink("/about", "About", options.current === "about")}
    ${navLink("/glossary", "Glossary", options.current === "glossary")}
    <button class="btn btn--icon" id="theme-toggle" type="button" hidden></button>
  </header>`;
}

/**
 * One nav item.
 *
 * Real hrefs, so middle-click and cmd-click work — `/glossary` is a separate
 * document at the edge (`infra/lib/site-stack.ts`), not a route into the app,
 * and `/` is the app's own empty state.
 *
 * `aria-current="page"` and not a visual "you are here" treatment. Two items is
 * short enough that a reader can see which page they are on from the page; the
 * attribute is for the reader who cannot.
 */
function navLink(href: string, label: string, current: boolean): string {
  return `<a class="header-link" href="${href}"${current ? ' aria-current="page"' : ""}>${label}</a>`;
}

/**
 * The footer.
 *
 * One line where two paragraphs were. The etiquette detail sits behind the
 * glyph — it is aimed at other developers, who will open it — while the privacy
 * claim stays visible, because a promise you have to discover is worth less
 * than one you are told, and the reader who most needs it is exactly the one
 * who will never hover.
 *
 * The mascot sits at this end and the Precisit mark at the other, on purpose:
 * this end is the thanks, that end is the credit. Hugging Face serves every
 * byte this tool reads, for free.
 */
export function footerHtml(options: ChromeOptions): string {
  const etiquette =
    options.etiquetteHref === undefined
      ? explainIcon("etiquette")
      : explainLink("etiquette", options.etiquetteHref);

  return `<footer class="app-footer">
    <p class="footer-thanks">
      <!--
        Only the words are the link. The mascot and the heart are sentiment, and
        a link whose hit area includes decoration gives a screen reader a
        confusing accessible name and gives a mouse user a target that is mostly
        not the thing they meant to click.
      -->
      <span class="hf-love">
        <img class="mascot" src="/precisit-astronaut.png" alt="" width="44" height="30" />
        <span class="heart" aria-hidden="true">❤️</span>
        <a href="https://huggingface.co">Hugging Face</a>
      </span>
      <span class="footer-sep" aria-hidden="true"></span>
      <span class="footer-claim">No accounts, no tracking, nothing downloaded</span>${etiquette}
      <!--
        Named in full, not "Terms" and not "HF Terms". An unqualified "Terms" in
        a footer means the site's own, and TensorLens has none — implying
        otherwise is a small lie in the one place people look for the honest
        version. "HF" would fix the ownership and reintroduce jargon on a page
        that just spent two paragraphs avoiding it.
      -->
      <a class="footer-claim" href="https://huggingface.co/terms-of-service"
        >Hugging Face terms</a
      >
    </p>
    <!--
      Attribution, matching the sibling sites. Two raster files swapped by theme
      rather than one recoloured SVG, because the mark is not ours to redraw —
      same approach corslabs uses. Dimensions are the true 300x97 ratio; corslabs
      declares 120x32 on the same source, which distorts it slightly.

      A CSS background rather than two img elements (2026-08-20). Both files
      used to sit in the DOM with CSS hiding one, and a browser fetches both
      whatever "display" says — every visitor paid 13 KB + 8 KB to use one of
      them. A background is fetched only when its rule applies, so the theme
      picks the file and only that file is downloaded.

      A picture element would be the obvious fix and cannot be used: its media
      attribute sees prefers-color-scheme but not data-theme, so the manual
      toggle would leave the wrong mark on screen. The three-state rule only
      exists in CSS, so the swap has to happen there too. role="img" plus
      aria-label because a background carries no alt text.

      (No backticks in this comment: it sits inside a template literal.)
    -->
    <p class="footer-attribution">
      <span>A free tool by</span>
      <a class="precisit-link" href="https://precisit.com" target="_blank"
         rel="noopener noreferrer" aria-label="Precisit (opens in a new tab)">
        <span class="precisit-mark" role="img" aria-label="Precisit"></span>
      </a>
    </p>
  </footer>`;
}

/**
 * The tagline is the one caller-supplied string in this file.
 *
 * It comes from a call site in this repo today, so this is not defending
 * against an attacker — it is defending against the next tagline containing an
 * ampersand and silently becoming an entity, which is the realistic failure.
 */
function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
